import React, { useEffect, useState } from "react";
import {
  Calendar as BigCalendar,
  dateFnsLocalizer,
  CalendarProps,
  Event,
} from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import config from "../config";
import { authFetch } from "../utils/Security/authFetch";
import { TaskData } from "../utils/Task/taskTypes";
import { ChangeData } from "../utils/Change/changeTypes";
import "./CalendarPage.css";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

type ExtendedCalendarProps = CalendarProps & {
  components?: Record<string, any>;
};

const Calendar = BigCalendar as React.ComponentType<ExtendedCalendarProps>;
type CalendarItem = {
  ticketId: string;
  status: string;
  closureCode?: string;
  type: "task" | "change";
  start: Date;
  end: Date;
  allDay?: boolean;
  spanLine?: boolean;
  rangeStart?: Date;
  rangeEnd?: Date;
  affectedCustomer: string[];
  overlapIndex?: number;
  totalOverlaps?: number;
  dayKey?: string;
};

const formatDateTime = (d?: Date) => (d ? format(d, "dd MMM yyyy, HH:mm") : "-");

const isMidnight = (d: Date) =>
  d.getHours() === 0 &&
  d.getMinutes() === 0 &&
  d.getSeconds() === 0 &&
  d.getMilliseconds() === 0;

// Renders event content; span lines get a hover card with the item's details.
const CalendarEvent: React.FC<{ event: Event; title: string }> = ({
  event,
  title,
}) => {
  const item = event.resource as CalendarItem;
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  if (!item.spanLine) return <span>{title}</span>;

  const rows: [string, string][] = [
    ["From", formatDateTime(item.rangeStart)],
    ["To", formatDateTime(item.rangeEnd)],
    ["Type", item.type === "change" ? "Change" : "Task"],
    ["Customer", item.affectedCustomer.join(", ") || "-"],
    ["Status", item.status || "-"],
    ...(item.closureCode
      ? [["Closure", item.closureCode] as [string, string]]
      : []),
  ];

  // Open the card below the cursor in the top half of the screen, above it in
  // the bottom half, so it never runs off-screen.
  const placement = pos
    ? pos.y < window.innerHeight / 2
      ? { top: pos.y + 16 }
      : { bottom: window.innerHeight - pos.y + 16 }
    : {};

  return (
    <div
      className="calendar-span-line-hit"
      onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setPos(null)}
    >
      {title}
      {pos &&
        createPortal(
          <div
            className="calendar-span-tooltip"
            style={{
              left: Math.min(pos.x + 12, window.innerWidth - 300),
              ...placement,
            }}
          >
            <div className="calendar-span-tooltip-title">{item.ticketId}</div>
            {rows.map(([label, value]) => (
              <div key={label} className="calendar-span-tooltip-row">
                <span>{label}</span>
                <span>{value}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  type CalendarView = "month" | "week" | "day" | "agenda";
  const [view, setView] = useState<CalendarView>("week");
  const [date, setDate] = useState<Date>(new Date());
  const [rowData, setRowData] = useState<CalendarItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const userId = localStorage.getItem("userid");

  const normalizeDate = (start: Date, end: Date) => {
    const normalizedEnd = new Date(end);
    if (normalizedEnd.getTime() < start.getTime()) {
      normalizedEnd.setDate(normalizedEnd.getDate() + 1);
    }
    return normalizedEnd;
  };

  const toDayKey = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d
      .getDate()
      .toString()
      .padStart(2, "0")}`;

  const splitMultiDayEvents = (events: CalendarItem[]): CalendarItem[] => {
    const expanded: CalendarItem[] = [];

    events.forEach((event) => {
      const originalStart = new Date(event.start);
      const originalEnd = new Date(event.end);

      const startDay = new Date(
        originalStart.getFullYear(),
        originalStart.getMonth(),
        originalStart.getDate(),
      );

      const endDay = new Date(
        originalEnd.getFullYear(),
        originalEnd.getMonth(),
        originalEnd.getDate(),
      );

      // Same-day event
      if (startDay.getTime() === endDay.getTime()) {
        expanded.push({
          ...event,
          start: originalStart,
          end: originalEnd,
          dayKey: toDayKey(originalStart),
          allDay: false,
        });
        return;
      }

      // Multi-day event: real blocks on the start and end days only, plus one
      // all-day "span line" across the top row from the day after the start
      // day. The line includes the end day only when the end day is fully
      // covered (ends at 23:59 or later); otherwise it stops the day before.
      const startDayEnd = new Date(startDay);
      startDayEnd.setHours(23, 59, 59, 999);

      const lineStart = new Date(startDay);
      lineStart.setDate(lineStart.getDate() + 1);

      const endDayFullyCovered =
        originalEnd.getHours() === 23 && originalEnd.getMinutes() === 59;

      const lineEnd = new Date(endDay); // exclusive
      if (endDayFullyCovered) lineEnd.setDate(lineEnd.getDate() + 1);

      expanded.push({
        ...event,
        start: originalStart,
        end: startDayEnd,
        allDay: false,
        dayKey: toDayKey(startDay),
      });

      // Ending exactly at midnight leaves nothing to draw on the end day. A
      // 00:00-00:00 block would also be treated as all-day by the calendar
      // and land in the top row.
      if (originalEnd > endDay) {
        expanded.push({
          ...event,
          start: new Date(endDay),
          end: originalEnd,
          allDay: false,
          dayKey: toDayKey(endDay),
        });
      }

      // No full day in between (e.g. Mon 14:00 -> Tue 10:00): no line.
      if (lineEnd > lineStart) {
        expanded.push({
          ...event,
          start: lineStart,
          end: lineEnd,
          allDay: true,
          spanLine: true,
          rangeStart: originalStart,
          rangeEnd: originalEnd,
          dayKey: toDayKey(lineStart),
        });
      }
    });

    return expanded;
  };

  const assignOverlapIndexes = (items: CalendarItem[]): CalendarItem[] => {
    const groups = new Map<string, CalendarItem[]>();

    items.forEach((it) => {
      const day = it.dayKey ?? toDayKey(it.start);
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day)!.push({ ...it, dayKey: day });
    });

    const adjusted: CalendarItem[] = [];

    groups.forEach((group) => {
      group.sort((a, b) => a.start.getTime() - b.start.getTime());

      const columns: Date[] = [];

      group.forEach((event) => {
        let placedIndex = -1;

        for (let i = 0; i < columns.length; i++) {
          if (columns[i].getTime() <= event.start.getTime()) {
            placedIndex = i;
            columns[i] = new Date(event.end);
            break;
          }
        }

        if (placedIndex === -1) {
          placedIndex = columns.length;
          columns.push(new Date(event.end));
        }

        adjusted.push({
          ...event,
          overlapIndex: placedIndex,
          totalOverlaps: columns.length,
        });
      });
    });

    return adjusted;
  };

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const taskUrl = isAdmin
          ? `${config.apiBaseUrl}/api/tasks/getAllCalendarTask`
          : `${config.apiBaseUrl}/api/tasks/user/${userId}`;

        const taskResponse = await authFetch(taskUrl);
        let tasks: CalendarItem[] = [];

        if (taskResponse.ok) {
          const data = await taskResponse.json();
          tasks = data.map((item: TaskData) => {
            const start = new Date(item.taskStartDatetime);
            const endRaw = item.taskCloseDatetime
              ? new Date(item.taskCloseDatetime)
              : start;
            const end = normalizeDate(start, endRaw);

            return {
              ticketId: item.ticketId,
              affectedCustomer: item.affectedCustomer || [],
              status: item.status,
              closureCode: item.closureCode, // ✅
              type: "task",
              start,
              end,
            };
          });
        }

        const changeUrl = isAdmin
          ? `${config.apiBaseUrl}/api/changes`
          : `${config.apiBaseUrl}/api/changes/user/${userId}`;

        const changeResponse = await authFetch(changeUrl);
        let changes: CalendarItem[] = [];

        if (changeResponse.ok) {
          const data = await changeResponse.json();
          changes = data.map((item: ChangeData) => {
            const start = item.changeStartDatetime
              ? new Date(item.changeStartDatetime)
              : new Date();

            const endRaw = item.changeCloseDatetime
              ? new Date(item.changeCloseDatetime)
              : start;

            const end = normalizeDate(start, endRaw);

            return {
              ticketId: item.ticketId,
              affectedCustomer: item.affectedCustomer || [],
              status: item.status,
              closureCode: item.closureCode, // ✅
              type: "change",
              start,
              end,
            };
          });
        }

        const split = splitMultiDayEvents([...tasks, ...changes]);
        const combined = [
          ...assignOverlapIndexes(split.filter((i) => !i.spanLine)),
          ...split.filter((i) => i.spanLine),
        ];

        if (mounted) setRowData(combined);
      } catch (error) {
        console.error("Error fetching calendar data:", error);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [isAdmin, userId]);

  const filteredRowData = rowData.filter((item) => {
    const query = searchQuery.toLowerCase();
    return (
      item.ticketId.toLowerCase().includes(query) ||
      item.affectedCustomer.some((c) => c.toLowerCase().includes(query))
    );
  });

  const events = filteredRowData.map((item) => ({
    id: item.ticketId,
    title: `${item.ticketId} - ${item.affectedCustomer.join(", ")}`,
    start: item.start,
    end: item.end,
    allDay: item.allDay || false,
    resource: item,
  }));

  const eventStyleGetter = (event: Event) => {
    const resource = event.resource as CalendarItem;
    const {
      status,
      closureCode,
      overlapIndex = 0,
      totalOverlaps = 1,
      dayKey = "",
    } = resource;

    const normalizedStatus = status?.toLowerCase();
    const normalizedClosure = closureCode?.toLowerCase();

    let baseColor = "#ff7f50";

    if (normalizedStatus === "closed") baseColor = "#28a745";

    if (
      normalizedClosure === "cancelled" ||
      normalizedClosure === "canceled" ||
      normalizedClosure === "rejected"
    ) {
      baseColor = "#9e9e9e"; // ✅ GREY
    }

    const dayHash =
      dayKey.split("-").reduce((acc, v) => acc + parseInt(v, 10), 0) % 5;

    const variation = (overlapIndex + dayHash) % 5;

    const adjustColor = (color: string, amount: number) => {
      let col = color.replace("#", "");
      const num = parseInt(col, 16);
      let r = (num >> 16) + amount;
      let g = ((num >> 8) & 0xff) + amount;
      let b = (num & 0xff) + amount;
      return `rgb(${Math.max(0, Math.min(255, r))},${Math.max(
        0,
        Math.min(255, g),
      )},${Math.max(0, Math.min(255, b))})`;
    };

    const adjustedColor = adjustColor(baseColor, variation * 10 - 20);

    const isGrey =
      normalizedClosure === "cancelled" ||
      normalizedClosure === "canceled" ||
      normalizedClosure === "rejected";

    // Anything the calendar puts in the top row (span lines, and items that
    // start and end at midnight) must stay in normal flow: the absolute
    // time-grid positioning below would stretch it over the whole row and
    // hide other lines.
    if (
      resource.spanLine ||
      (isMidnight(resource.start) && isMidnight(resource.end))
    ) {
      return {
        className: "calendar-span-line",
        style: {
          backgroundColor: adjustedColor,
          opacity: isGrey ? 0.75 : 1,
        },
      };
    }

    const gap = 4;
    const widthPercent = Math.max(
      6,
      (100 - gap * (totalOverlaps - 1)) / totalOverlaps,
    );
    const leftPercent = (widthPercent + gap) * overlapIndex;

    return {
      style: {
        backgroundColor: adjustedColor,
        color: "white",
        borderRadius: "5px",
        position: "absolute",
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        padding: "2px 4px",
        fontSize: "12px",
        opacity: isGrey ? 0.75 : 1,
        border: "none",
      },
    };
  };

  const handleEventSelect = (event: any) => {
    const ticketId = event.id;
    const tl = ticketId.toLowerCase();

    if (tl.startsWith("tsk")) navigate(`/task-edit/${ticketId}`);
    else if (tl.startsWith("chn")) navigate(`/change-edit/${ticketId}`);
    else navigate(`/task-edit/${ticketId}`);
  };

  const btnStyle = {
    background: "#ff7f50",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "6px 12px",
    cursor: "pointer",
  };

  const CustomToolbar = ({ label, onNavigate, onView }: any) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button style={btnStyle} onClick={() => onNavigate("PREV")}>
          ‹ Back
        </button>
        <button style={btnStyle} onClick={() => onNavigate("TODAY")}>
          Today
        </button>
        <button style={btnStyle} onClick={() => onNavigate("NEXT")}>
          Next ›
        </button>
      </div>

      <div style={{ textAlign: "center", fontWeight: "bold" }}>{label}</div>

      <div style={{ textAlign: "center" }}>
        {(["month", "week", "day", "agenda"] as CalendarView[]).map((v) => (
          <button
            key={v}
            style={{
              ...btnStyle,
              margin: "0 5px",
              background: view === v ? "#ff7f50" : "#e0e0e0",
              color: view === v ? "white" : "#333",
            }}
            onClick={() => onView(v)}
          >
            {v.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div
      style={{
        height: "100vh",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <input
        type="text"
        placeholder="Search by ticket ID or customer..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <div style={{ flex: 1 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
          view={view}
          onView={(newView) => setView(newView as CalendarView)}
          date={date}
          onNavigate={(newDate: Date) => setDate(newDate)}
          selectable
          views={["month", "week", "day", "agenda"]}
          eventPropGetter={eventStyleGetter}
          components={{ toolbar: CustomToolbar, event: CalendarEvent }}
          tooltipAccessor={(e: Event) =>
            (e.resource as CalendarItem).spanLine ? "" : (e.title as string)
          }
          onSelectEvent={handleEventSelect}
          {...({ showMultiDayTimes: true } as any)}
        />
      </div>
    </div>
  );
};

export default CalendarPage;
